import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useTelegram } from '@/hooks/useTelegram';
import {
  getSubscriptionPlans,
  getCurrentSubscription,
  cancelAutoRenew,
  type SubscriptionPlan,
  type Subscription,
} from '@/services/subscriptions';
import {
  getPaymentMethods,
  createPayKeeperPayment,
  createStarsPayment,
  type PaymentMethod,
} from '@/services/payments';

interface SubscriptionPageProps {
  onClose: () => void;
}

export function SubscriptionPage({ onClose }: SubscriptionPageProps) {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentSub, setCurrentSub] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'paykeeper' | 'stars'>('paykeeper');
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  
  const { hapticFeedback, openLink } = useTelegram();
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      setIsLoading(true);
      const [plansRes, subRes, methodsRes] = await Promise.all([
        getSubscriptionPlans(),
        getCurrentSubscription(),
        getPaymentMethods(),
      ]);
      setPlans(plansRes.plans);
      setCurrentSub(subRes.subscription);
      setPaymentMethods(methodsRes.methods);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSubscribe = async () => {
    if (!selectedPlan) return;
    
    // Показываем модал выбора метода оплаты
    setShowPaymentMethodModal(true);
  };
  
  const handlePayment = async () => {
    if (!selectedPlan) return;
    
    try {
      setIsSubscribing(true);
      setShowPaymentMethodModal(false);
      
      if (selectedPaymentMethod === 'stars') {
        // Оплата через Telegram Stars
        const result = await createStarsPayment(selectedPlan.id, billingPeriod);
        hapticFeedback('success');
        
        // Открываем invoice link в Telegram
        if (openLink) {
          openLink(result.invoiceLink);
        } else {
          window.open(result.invoiceLink, '_blank');
        }
      } else {
        // Оплата через PayKeeper
        const result = await createPayKeeperPayment(selectedPlan.id, billingPeriod);
        hapticFeedback('success');
        
        // Редирект на страницу оплаты
        window.location.href = result.paymentUrl;
      }
      
      setSelectedPlan(null);
    } catch (error: any) {
      hapticFeedback('error');
      alert(error.message);
    } finally {
      setIsSubscribing(false);
    }
  };
  
  const handleCancelRenewal = async () => {
    if (!confirm('Отменить автопродление? Подписка останется активной до конца оплаченного периода.')) {
      return;
    }
    
    try {
      setIsCancelling(true);
      await cancelAutoRenew();
      hapticFeedback('light');
      await loadData();
    } catch (error: any) {
      hapticFeedback('error');
      alert(error.message);
    } finally {
      setIsCancelling(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-primary-950 flex flex-col safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-primary-800/50">
        <button onClick={onClose} className="text-primary-400">
          ← Назад
        </button>
        <h1 className="text-lg font-semibold text-white">Подписка</h1>
        <div className="w-16" />
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current Subscription */}
            {currentSub && (
              <CurrentSubscriptionCard
                subscription={currentSub}
                onCancelRenewal={handleCancelRenewal}
                isCancelling={isCancelling}
              />
            )}
            
            {/* Billing Toggle */}
            <BillingToggle
              value={billingPeriod}
              onChange={setBillingPeriod}
            />
            
            {/* Plans */}
            <div className="space-y-4">
              {plans.map(plan => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  billingPeriod={billingPeriod}
                  onSelect={() => setSelectedPlan(plan)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Plan Detail Modal */}
      <AnimatePresence>
        {selectedPlan && !showPaymentMethodModal && (
          <PlanDetailModal
            plan={selectedPlan}
            billingPeriod={billingPeriod}
            onClose={() => setSelectedPlan(null)}
            onSubscribe={handleSubscribe}
            isSubscribing={isSubscribing}
          />
        )}
      </AnimatePresence>
      
      {/* Payment Method Modal */}
      <AnimatePresence>
        {showPaymentMethodModal && selectedPlan && (
          <PaymentMethodModal
            plan={selectedPlan}
            billingPeriod={billingPeriod}
            methods={paymentMethods}
            selectedMethod={selectedPaymentMethod}
            onSelectMethod={setSelectedPaymentMethod}
            onClose={() => setShowPaymentMethodModal(false)}
            onPay={handlePayment}
            isProcessing={isSubscribing}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// CURRENT SUBSCRIPTION CARD
// ============================================

interface CurrentSubscriptionCardProps {
  subscription: Subscription;
  onCancelRenewal: () => void;
  isCancelling: boolean;
}

function CurrentSubscriptionCard({ subscription, onCancelRenewal, isCancelling }: CurrentSubscriptionCardProps) {
  const endDate = subscription.endDate 
    ? new Date(subscription.endDate).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : 'Бессрочно';
  
  return (
    <div className="bg-gradient-to-br from-primary-800/70 to-primary-900/70 border border-primary-700/50 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-primary-400">Текущий план</p>
          <h3 className="text-xl font-bold text-white">{subscription.plan.name}</h3>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm ${
          subscription.status === 'active' 
            ? 'bg-green-500/20 text-green-400' 
            : 'bg-yellow-500/20 text-yellow-400'
        }`}>
          {subscription.status === 'active' ? 'Активна' : 'Истекает'}
        </div>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex justify-between text-primary-300">
          <span>Действует до</span>
          <span>{endDate}</span>
        </div>
        
        {subscription.daysRemaining > 0 && (
          <div className="flex justify-between text-primary-300">
            <span>Осталось дней</span>
            <span className={subscription.isExpiringSoon ? 'text-yellow-400' : ''}>
              {subscription.daysRemaining}
            </span>
          </div>
        )}
        
        <div className="flex justify-between text-primary-300">
          <span>Автопродление</span>
          <span>{subscription.autoRenew ? 'Включено' : 'Выключено'}</span>
        </div>
      </div>
      
      {subscription.autoRenew && (
        <button
          onClick={onCancelRenewal}
          disabled={isCancelling}
          className="mt-4 w-full py-2 text-sm text-primary-400 hover:text-primary-300 disabled:opacity-50"
        >
          {isCancelling ? 'Отменяем...' : 'Отменить автопродление'}
        </button>
      )}
    </div>
  );
}

// ============================================
// BILLING TOGGLE
// ============================================

interface BillingToggleProps {
  value: 'monthly' | 'yearly';
  onChange: (value: 'monthly' | 'yearly') => void;
}

function BillingToggle({ value, onChange }: BillingToggleProps) {
  return (
    <div className="flex items-center justify-center gap-2 p-1 bg-primary-800/50 rounded-xl">
      <button
        onClick={() => onChange('monthly')}
        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
          value === 'monthly'
            ? 'bg-primary-700 text-white'
            : 'text-primary-400'
        }`}
      >
        Месяц
      </button>
      <button
        onClick={() => onChange('yearly')}
        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
          value === 'yearly'
            ? 'bg-primary-700 text-white'
            : 'text-primary-400'
        }`}
      >
        Год <span className="text-green-400 text-xs">−20%</span>
      </button>
    </div>
  );
}

// ============================================
// PLAN CARD
// ============================================

interface PlanCardProps {
  plan: SubscriptionPlan;
  billingPeriod: 'monthly' | 'yearly';
  onSelect: () => void;
}

function PlanCard({ plan, billingPeriod, onSelect }: PlanCardProps) {
  const monthlyPrice = billingPeriod === 'yearly'
    ? Math.round(plan.priceYearly / 12)
    : plan.priceMonthly;
  
  const isFreePlan = plan.sortOrder === 0;
  const isPremium = plan.sortOrder >= 2;
  
  return (
    <motion.button
      whileTap={{ scale: 0.98 }}
      onClick={onSelect}
      disabled={plan.isCurrent}
      className={`w-full p-5 rounded-2xl text-left transition-all ${
        isPremium
          ? 'bg-gradient-to-br from-amber-900/30 to-amber-800/20 border-2 border-amber-700/50'
          : plan.isCurrent
            ? 'bg-primary-800/50 border-2 border-primary-500'
            : 'bg-primary-800/50 border border-primary-700/50 hover:border-primary-600/50'
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-white">{plan.name}</h3>
            {isPremium && <span className="text-amber-400">⭐</span>}
            {plan.isCurrent && (
              <span className="px-2 py-0.5 bg-primary-500/30 text-primary-300 text-xs rounded">
                Текущий
              </span>
            )}
          </div>
          {plan.description && (
            <p className="text-sm text-primary-400 mt-1">{plan.description}</p>
          )}
        </div>
        
        <div className="text-right">
          {isFreePlan ? (
            <p className="text-xl font-bold text-white">Бесплатно</p>
          ) : (
            <>
              <p className="text-xl font-bold text-white">{monthlyPrice} ₽</p>
              <p className="text-xs text-primary-500">в месяц</p>
            </>
          )}
        </div>
      </div>
      
      {/* Features preview */}
      <div className="flex flex-wrap gap-2">
        {plan.features.slice(0, 3).map((feature, i) => (
          <span
            key={i}
            className="px-2 py-1 bg-primary-900/50 text-primary-300 text-xs rounded"
          >
            {feature}
          </span>
        ))}
        {plan.features.length > 3 && (
          <span className="px-2 py-1 text-primary-500 text-xs">
            +{plan.features.length - 3}
          </span>
        )}
      </div>
    </motion.button>
  );
}

// ============================================
// PLAN DETAIL MODAL
// ============================================

interface PlanDetailModalProps {
  plan: SubscriptionPlan;
  billingPeriod: 'monthly' | 'yearly';
  onClose: () => void;
  onSubscribe: () => void;
  isSubscribing: boolean;
}

function PlanDetailModal({ plan, billingPeriod, onClose, onSubscribe, isSubscribing }: PlanDetailModalProps) {
  const price = billingPeriod === 'monthly' ? plan.priceMonthly : plan.priceYearly;
  const monthlyPrice = billingPeriod === 'yearly' 
    ? Math.round(plan.priceYearly / 12) 
    : plan.priceMonthly;
  const isFreePlan = plan.sortOrder === 0;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-primary-900 rounded-t-3xl w-full max-w-lg max-h-[85vh] flex flex-col safe-area-bottom"
      >
        <div className="p-6 border-b border-primary-800/50">
          <div className="w-12 h-1 bg-primary-700 rounded-full mx-auto mb-4" />
          
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white">{plan.name}</h2>
            {!isFreePlan && (
              <div className="mt-2">
                <span className="text-3xl font-bold text-white">{monthlyPrice} ₽</span>
                <span className="text-primary-400">/мес</span>
                {billingPeriod === 'yearly' && (
                  <p className="text-sm text-primary-500 mt-1">
                    {price} ₽ в год (экономия 20%)
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          <h3 className="text-sm font-medium text-primary-400 mb-3">Что включено:</h3>
          <ul className="space-y-3">
            {plan.features.map((feature, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-green-400 mt-0.5">✓</span>
                <span className="text-primary-200">{feature}</span>
              </li>
            ))}
          </ul>
          
          {Object.keys(plan.limits).length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-primary-400 mb-3">Лимиты:</h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(plan.limits).map(([key, value]) => (
                  <div key={key} className="bg-primary-800/30 rounded-lg p-3">
                    <p className="text-lg font-bold text-white">
                      {value === -1 ? '∞' : value}
                    </p>
                    <p className="text-xs text-primary-500">{getLimitLabel(key)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="p-6 border-t border-primary-800/50 space-y-3">
          {plan.isCurrent ? (
            <div className="text-center py-3 text-primary-400">
              Это твой текущий план
            </div>
          ) : isFreePlan ? (
            <div className="text-center py-3 text-primary-500 text-sm">
              Бесплатный план активен по умолчанию
            </div>
          ) : (
            <Button
              onClick={onSubscribe}
              loading={isSubscribing}
              fullWidth
            >
              {plan.isUpgrade ? 'Улучшить план' : 'Выбрать план'}
            </Button>
          )}
          
          <Button onClick={onClose} variant="ghost" fullWidth>
            Закрыть
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// HELPERS
// ============================================

function getLimitLabel(key: string): string {
  const labels: Record<string, string> = {
    dailyActivities: 'Активностей/день',
    skillSlots: 'Слотов навыков',
    guildMembership: 'Гильдий',
    eventCheckins: 'Чекинов/день',
    daemonMessages: 'Сообщений Демону',
  };
  return labels[key] || key;
}

// ============================================
// PAYMENT METHOD MODAL
// ============================================

interface PaymentMethodModalProps {
  plan: SubscriptionPlan;
  billingPeriod: 'monthly' | 'yearly';
  methods: PaymentMethod[];
  selectedMethod: 'paykeeper' | 'stars';
  onSelectMethod: (method: 'paykeeper' | 'stars') => void;
  onClose: () => void;
  onPay: () => void;
  isProcessing: boolean;
}

function PaymentMethodModal({
  plan,
  billingPeriod,
  methods,
  selectedMethod,
  onSelectMethod,
  onClose,
  onPay,
  isProcessing,
}: PaymentMethodModalProps) {
  const price = billingPeriod === 'monthly' ? plan.priceMonthly : plan.priceYearly;
  const starsPrice = Math.ceil(price / 1.5); // Примерный курс
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-primary-900 rounded-t-3xl w-full max-w-lg safe-area-bottom"
      >
        <div className="p-6">
          <div className="w-12 h-1 bg-primary-700 rounded-full mx-auto mb-4" />
          
          <h2 className="text-xl font-bold text-white text-center mb-2">
            Выберите способ оплаты
          </h2>
          <p className="text-primary-400 text-center text-sm mb-6">
            {plan.name} • {billingPeriod === 'monthly' ? '1 месяц' : '1 год'}
          </p>
          
          <div className="space-y-3 mb-6">
            {methods.map((method) => (
              <button
                key={method.id}
                onClick={() => onSelectMethod(method.id as 'paykeeper' | 'stars')}
                disabled={!method.available}
                className={`w-full p-4 rounded-xl text-left transition-all flex items-center gap-4 ${
                  selectedMethod === method.id
                    ? 'bg-primary-700 border-2 border-primary-500'
                    : 'bg-primary-800/50 border border-primary-700/50'
                } ${!method.available ? 'opacity-50' : ''}`}
              >
                <span className="text-2xl">{method.icon}</span>
                <div className="flex-1">
                  <p className="font-medium text-white">{method.name}</p>
                  <p className="text-sm text-primary-400">{method.description}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-white">
                    {method.id === 'stars' ? `${starsPrice} ⭐` : `${price} ₽`}
                  </p>
                </div>
                {selectedMethod === method.id && (
                  <span className="text-green-400">✓</span>
                )}
              </button>
            ))}
          </div>
          
          <div className="space-y-3">
            <Button
              onClick={onPay}
              loading={isProcessing}
              fullWidth
            >
              Оплатить {selectedMethod === 'stars' ? `${starsPrice} ⭐` : `${price} ₽`}
            </Button>
            
            <Button onClick={onClose} variant="ghost" fullWidth>
              Назад
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
